import { Alert, AuthSurface, Badge, Module, ModuleBody, StatusDot } from "@vjal/ui";
import type { ReactElement } from "react";
import type { DataTableColumn } from "@vjal/ui";
import { useState } from "react";
import type { RouteAccessDecision } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { isLiveMode } from "../lib/config";
import { humanizeStatus } from "../lib/status-format";
import { useAuthOtpMutations } from "../hooks/useCoachingQueries";
import {
  backendBlockedScreen,
  meetingsAliasScreen,
  resolveScreenContract
} from "./product-contract-guardrails";
import { transcriptDetailScreen } from "./product-artifact-surfaces";
import { reportDetailScreen, reportLibraryScreen, sessionReportScreen } from "./product-reports-surfaces";
import { orgAdminHomeScreen } from "./product-org-admin-surfaces";
import { orgReportsScreen } from "./product-org-reports-surfaces";
import { cohortCreateScreen, cohortListScreen, learnerCohortsScreen } from "./product-cohort-management-surfaces";
import { cohortWorkspaceScreen } from "./product-detail-surfaces";
import {
  calendarScreen,
  clearSeriesScreen,
  createSeriesScreen,
  sessionDetailScreen,
  sessionsListScreen,
  SessionsPage
} from "./product-sessions-surfaces";
import { dashboardScreen } from "./product-dashboard-surface";
import { exportsScreen, logsScreen, thresholdsScreen, toolViewScreen } from "./product-logs-exports-surfaces";
import { companyDetailScreen, companyWorkspaceScreen, programDetailScreen, programWorkspaceScreen } from "./product-organization-surfaces";
import { participantManagementScreen, participantDetailScreen } from "./product-participant-management-surfaces";
import { bulkParticipantsScreen } from "./product-participant-surfaces";
import { profileSettingsScreen } from "./product-profile-surfaces";
import { resourceDetailScreen, resourceLibraryScreen } from "./product-resource-surfaces";
import { capstoneScreen, capstoneSubmissionsScreen } from "./product-capstone-surfaces";
import { certificatesScreen } from "./product-certificate-surfaces";
import { userEditScreen } from "./product-user-edit-surface";
import { sessionInvitesScreen } from "./product-session-invites-surfaces";
import { rescheduleScreen } from "./product-session-reschedule-surfaces";
import { userDetailScreen, userManagementScreen } from "./product-user-management-surfaces";

import { PlatformOrganizationsSurface } from "./product-platform-organizations-surface";
export { cohorts, companies, reports, sessions } from "./product-route-data";
export type TableRow = Record<string, unknown> & { id: string };

export interface ProductScreenContext {
  role: CoachingRole;
  decision: RouteAccessDecision;
  routePath: string;
}

export interface DecisionHierarchy {
  firstViewport: string;
  primaryAction: string;
  secondaryPanel: string;
  deferredSurface: string;
  gatedControls: string;
}

export interface ProductScreenContract {
  title: (role: CoachingRole) => string;
  primaryJob: string;
  secondaryJob: string;
  workTitle: string;
  rows: TableRow[];
  columns: DataTableColumn<TableRow>[];
  states: string[];
  apiFamilies: string[];
  roleDeltas: string;
  extensionSlots: string[];
  mobilePosture: string;
  evidenceRequirement: string;
  vjalComponents?: string[];
  localCompositions?: string[];
  packageGapRefs?: string[];
  decisionHierarchy?: DecisionHierarchy;
  requiredViewports?: string[];
  mobileEvidencePaths?: string[];
  screenEvidencePaths?: string[];
  surfaceKind?: "product" | "backend-blocked" | "compatibility-alias";
  surfaceMode?: "default" | "focused";
  closureState?: "focused" | "foundation-shell" | "backend-blocked" | "compatibility-alias";
  /** Whether this role's custom body supplies the approved primary h1. */
  bodyOwnsPrimaryH1?: (role: CoachingRole) => boolean;
  closureOwner?: string;
  customBody?: (context: ProductScreenContext) => ReactElement;
}

export function LoginOtpSurface() {
  // Fixture defaults belong to mock/evidence runs only; live users start blank.
  const [email, setEmail] = useState(isLiveMode() ? "" : "operator@coaching.example");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "otp">("email");
  const auth = useAuthOtpMutations();
  const requestResult = auth.requestOtp.data;
  const verifyResult = auth.verifyOtp.data;
  const sendFailed = auth.requestOtp.isError || (requestResult && requestResult.status !== "requested");
  const invalidOtp = auth.verifyOtp.isError || verifyResult?.ok === false;
  const verified = verifyResult?.ok === true;

  const sendCode = () => {
    auth.verifyOtp.reset();
    auth.requestOtp.mutate(
      { email },
      { onSuccess: (result) => result.status === "requested" && setStep("otp") }
    );
  };

  const changeEmail = () => {
    auth.requestOtp.reset();
    auth.verifyOtp.reset();
    setOtp("");
    setStep("email");
  };

  const error =
    step === "email" && sendFailed
      ? "We couldn't send a code to that email. Check it and try again."
      : step === "otp" && invalidOtp
        ? "That code didn't work. Check the code and try again."
        : undefined;

  return (
    <Module span={12}>
      <ModuleBody data-testid="coaching-login-card" className="mx-auto w-full max-w-md space-y-4 py-8">
        {verified ? <Alert variant="success">You're signed in. Opening your workspace.</Alert> : null}
        {/* F3: real @vjal/ui Auth-1 recipe export (not marker-only chrome). */}
        <AuthSurface
          step={step}
          email={email}
          otp={otp}
          error={error}
          loading={auth.requestOtp.isPending || auth.verifyOtp.isPending}
          resendCooldownSeconds={0}
          title="Sign in"
          onEmailChange={setEmail}
          onOtpChange={setOtp}
          autoSubmitOnComplete
          onRequestOtp={sendCode}
          onVerifyOtp={(submittedOtp) => auth.verifyOtp.mutate({ email, otp: submittedOtp ?? otp })}
          onResend={sendCode}
          onChangeEmail={changeEmail}
        />
        <p className="m-0 text-sm text-ink-muted">
          Need help?{" "}
          <a className="text-ink-muted underline underline-offset-2 hover:text-ink" href="/user-manual/" rel="noopener noreferrer" target="_blank">
            User manual
          </a>
        </p>
      </ModuleBody>
    </Module>
  );
}

export function screenForRoute(routeId: string): ProductScreenContract {
  return resolveScreenContract(routeId, screenForRouteDraft(routeId));
}

function screenForRouteDraft(routeId: string): ProductScreenContract {
  if (routeId === "login") {
    return {
      title: () => "Sign in",
      primaryJob: "Sign in with your work email and a one-time code.",
      secondaryJob: "Sign-in problems never expose Coaching records.",
      workTitle: "Sign in",
      rows: [{ id: "login", state: "protected", behavior: "no product data before sign-in" }],
      columns: [
        { key: "state", header: "State" },
        { key: "behavior", header: "Behavior" }
      ],
      states: ["loading", "error", "denied"],
      apiFamilies: ["auth-service"],
      roleDeltas: "Signed-out users see no product navigation or Coaching records.",
      extensionSlots: ["logout redirect", "post-sign-in role landing"],
      mobilePosture: "Single-column sign-in form with visible email and code controls.",
      evidenceRequirement: "Playwright must cover the two-step sign-in flow with no product nav or records before sign-in completes.",
      surfaceMode: "focused",
      customBody: LoginOtpSurface
    };
  }

  if (routeId === "dashboard") {
    return dashboardScreen();
  }

  if (routeId === "cohort-session-series") return createSeriesScreen();
  if (routeId === "clear-series") return clearSeriesScreen();
  if (routeId === "calendar") return calendarScreen();
  if (routeId === "cohort-workspace") return cohortWorkspaceScreen();
  if (routeId === "cohort-create") return cohortCreateScreen();
  if (routeId === "cohorts") return cohortListScreen();
  if (routeId === "my-cohorts") return learnerCohortsScreen();
  if (routeId === "session-invites") return sessionInvitesScreen();
  if (routeId === "session-reschedule") return rescheduleScreen();
  if (routeId === "session-detail") return sessionDetailScreen();
  if (routeId === "users") return userManagementScreen();
  if (routeId === "user-detail") return userDetailScreen();
  if (routeId === "user-edit") return userEditScreen();
  if (routeId === "report-detail") return reportDetailScreen();
  if (routeId === "session-report") return sessionReportScreen();
  if (routeId === "transcript-detail") return transcriptDetailScreen();
  if (routeId === "resources") return resourceLibraryScreen();
  if (routeId === "resource-detail") return resourceDetailScreen();
  if (routeId === "capstone-submissions") return capstoneSubmissionsScreen();
  if (routeId === "capstone") return capstoneScreen();
  if (routeId === "certificates") return certificatesScreen();
  if (routeId === "cohort-bulk-participants") return bulkParticipantsScreen();
  // Wave 10: the admin Thresholds shell (not the participant profile settings).
  // MUST precede the generic `settings*` -> profile fallthrough below.
  if (routeId === "settings-thresholds") return thresholdsScreen();
  if (routeId === "tools" || routeId === "tool-view") return toolViewScreen();
  if (routeId.startsWith("settings") || routeId === "profile") return profileSettingsScreen();
  if (routeId === "company-detail") return companyDetailScreen();
  if (routeId === "companies" || routeId.startsWith("company")) return companyWorkspaceScreen();
  if (routeId === "program-detail") return programDetailScreen();
  if (routeId.startsWith("program")) return programWorkspaceScreen();
  if (routeId === "participant-detail") return participantDetailScreen();
  if (routeId.startsWith("participant")) return participantManagementScreen();
  if (routeId === "sessions") return sessionsListScreen();
  if (routeId === "meetings") {
    const sessionsContract = sessionsListScreen();
    return {
      ...meetingsAliasScreen(sessionColumns),
      customBody: SessionsPage,
      vjalComponents: sessionsContract.vjalComponents,
      localCompositions: ["sessions focused product surface", "compatibility alias contract"]
    };
  }
  if (routeId === "reports") return reportLibraryScreen();
  if (routeId === "organizations") return platformOrganizationsScreen();
  if (routeId === "org-admin-home") return orgAdminHomeScreen();
  if (routeId === "org-reports") return orgReportsScreen();
  if (routeId === "permissions") return backendBlockedScreen("Permissions", "super-admin-permission-management-backend");
  // Wave 10: Exports is now a REAL planned-screen (the CSV backend is live).
  if (routeId === "exports") return exportsScreen();

  // Wave 10: History is now the gold-pattern Logs surface (audit + log views).
  if (routeId === "history") return logsScreen();

  return {
    title: () => "Coaching screen",
    primaryJob: "Render the active route with VJAL product primitives and role-aware guardrails.",
    secondaryJob: "This surface is still being specialized.",
    workTitle: "Route evidence",
    rows: [{ id: routeId, route: routeId, status: "planned-screen" }],
    columns: [
      { key: "route", header: "Route" },
      { key: "status", header: "Status" }
    ],
    states: ["loading", "empty", "error", "denied"],
    apiFamilies: ["api-catalog-pending"],
    roleDeltas: "Route-specific role deltas must be filled before claiming workflow completeness.",
    extensionSlots: ["workflow-specific extension slot pending"],
    mobilePosture: "Route must receive a mobile no-overlap screenshot before release.",
    evidenceRequirement: "Route must have deterministic Playwright evidence or a ledger blocker."
  };
}

export function stateTone(state: string) {
  if (state === "error" || state === "denied") return "err";
  if (state === "read-only") return "info";
  return "idle";
}

function sessionColumns() {
  return [
    { key: "title", header: "Session" },
    { key: "cohort", header: "Cohort" },
    { key: "invite", header: "Invite", render: (row: TableRow) => <Badge variant="warning">{humanizeStatus(String(row.invite))}</Badge> },
    { key: "status", header: "Status", render: (row: TableRow) => <StatusDot tone="info" label={humanizeStatus(String(row.status))} /> }
  ];
}

function platformOrganizationsScreen(): ProductScreenContract {
  return {
    title: () => "Organisations",
    primaryJob: "Create Coaching organisations and inspect platform-wide enrolment visibility.",
    secondaryJob: "Platform-admin only. Invite delivery is reported honestly and never implied when no sender is configured.",
    workTitle: "Organisation directory",
    rows: [],
    columns: [],
    states: ["loading", "ready", "empty", "error", "denied", "invite-not-sent"],
    apiFamilies: ["platform-organizations-service"],
    roleDeltas: "Only platform administrators may open this route; the caller's Coaching grant role is shown per organisation.",
    extensionSlots: ["organisation switch", "invite delivery"],
    mobilePosture: "Search, directory metrics, and the creation form stack without horizontal overflow.",
    evidenceRequirement: "Prove platform-admin visibility, honest invite-not-sent state, search/sort, and switch-to-it navigation.",
    surfaceMode: "focused",
    customBody: PlatformOrganizationsSurface
  };
}
